/**
 * What the `src` of an image in agent markdown actually addresses.
 *
 * The agent writes internal tokens (`/browser-frames/{threadId}-{id}`,
 * `urn:bb:file:{id}`) rather than API paths because message content is
 * immutable: the route can move without breaking old threads, and no expiring
 * signed URL gets baked into a message that outlives it. Resolving the token is
 * therefore the client's job, and both clients have to agree on the grammar —
 * which is why this lives here rather than beside either renderer.
 */

import { fileArtifactIdFromImageSrc } from "@/types/fileImageSrc.ts";

/**
 * Reserved prefixes for internal image sources rather than real URLs.
 *
 * Active prefixes resolve to API paths. `/attachments/` is retired but stays
 * listed so tokens in old messages are recognized and dropped, rather than
 * falling through to `external` and being requested against the app's origin.
 */
export const INTERNAL_IMAGE_PREFIXES = ["/attachments/", "/browser-frames/"] as const;

/** Stands in when the markdown carried no alt text. Never render an unnamed image. */
export const BROWSER_FRAME_FALLBACK_ALT = "Browser screenshot";

export type MessageImageSource =
  /** A screenshot browser-use captured and kept in the thread. */
  | { kind: "browserFrame"; threadId: string; frameId: string }
  /**
   * A file artifact — an image the agent generated or the user uploaded —
   * addressed by its URN, the one handle the agent already holds for it.
   */
  | { kind: "fileArtifact"; artifactId: string }
  /** A real URL — a photo the agent found on the web. */
  | { kind: "external"; url: string }
  /**
   * A non-renderable internal source, such as a malformed token, a retired
   * attachment token or a URN that names no file. Distinct from `external` so
   * callers drop it rather than request the app's own origin and paint a
   * broken image.
   */
  | { kind: "invalid" };

/**
 * Split an internal `<prefix>/{threadId}-{id}` token, or `null` when `src`
 * doesn't carry that prefix or either half is missing.
 */
const parseThreadScopedToken = (
  src: string,
  prefix: string,
): { threadId: string; id: string } | null => {
  if (!src.startsWith(prefix)) return null;
  const [threadId, id] = (src.slice(prefix.length).split("/")[0] ?? "").split("-");
  if (!threadId || !id) return null;
  return { threadId, id };
};

/** Classify a markdown image `src`. */
export const resolveMessageImageSrc = (src: string): MessageImageSource => {
  const frame = parseThreadScopedToken(src, "/browser-frames/");
  if (frame) {
    return { kind: "browserFrame", threadId: frame.threadId, frameId: frame.id };
  }

  if (INTERNAL_IMAGE_PREFIXES.some((prefix) => src.startsWith(prefix))) {
    return { kind: "invalid" };
  }

  const fileArtifactId = fileArtifactIdFromImageSrc(src);
  if (fileArtifactId) return { kind: "fileArtifact", artifactId: fileArtifactId };

  // Only a file URN has bytes behind it. Any other URN names a page, which is
  // HTML, not a picture — so it is dropped rather than requested.
  if (src.startsWith("urn:")) return { kind: "invalid" };

  return { kind: "external", url: src };
};

/**
 * Server-written paths for a shared reader's file images, by file artifact id
 * (a shared document view's `imageSrcs`).
 */
export type ImageSrcOverrides = Readonly<Record<string, string>>;

/**
 * The path the server wrote for this image, if it wrote one. A shared reader's
 * session cannot reach the owner's routes, so only these load for them.
 */
export const authorizedImagePath = (
  source: MessageImageSource,
  imageSrcs?: ImageSrcOverrides,
): string | null =>
  source.kind === "fileArtifact" ? (imageSrcs?.[source.artifactId] ?? null) : null;

/**
 * The API path an internal image lives at, or the URL itself when the image is
 * external. `null` for a non-renderable internal source, such as a malformed or
 * retired token, which has nothing to request.
 *
 * Returns a path rather than a URL so each client can put it through its own
 * `resolveApiPath` — the two apps resolve against different origins.
 *
 * `width` is a render hint for the resizing endpoints. A path the server wrote
 * into `imageSrcs` wins and carries its own width.
 */
export const messageImagePath = (
  source: MessageImageSource,
  width: number,
  imageSrcs?: ImageSrcOverrides,
): string | null => {
  switch (source.kind) {
    case "browserFrame":
      return `/api/a/threads/${encodeURIComponent(source.threadId)}/browser-frames/${encodeURIComponent(source.frameId)}?w=${width}`;
    case "fileArtifact":
      // The artifact-keyed route rather than the owner's source-keyed one: the
      // URN carries the artifact id, and this route serves the owner and anyone
      // the file is shared with alike.
      return (
        authorizedImagePath(source, imageSrcs) ??
        `/api/a/artifacts/${encodeURIComponent(source.artifactId)}/filePreview?w=${width}`
      );
    case "external":
      return source.url;
    case "invalid":
      return null;
  }
};
